import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import groupBy from "lodash/groupBy";
import type { FC } from "react";
import { useEffect, useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import LabelledCheckbox from "../../components/labelledCheckbox/LabelledCheckbox";
import NumberField from "../../components/numberField/NumberField";
import { useProducts } from "../products/useProducts";
import DictionaryAccordion from "./DictionaryAccordion";
import type { CreateOrUpdateProductOptionRequestBody, ProductOption } from "./productOptionsApi";
import UsedByProducts from "./UsedByProducts";
import { useProductOptions } from "./useProductOptions";

interface OptionChange {
  readonly name: string;
  readonly roomConfig: ProductOption["roomConfig"] | null;
  readonly multiplier: number | null;
  readonly mandatory: boolean;
}

interface AddOptionFooterProps {
  readonly onAdd: (option: OptionChange) => Promise<void>;
}

const AddOptionFooter: FC<AddOptionFooterProps> = ({ onAdd }) => {
  const [name, setName] = useState("");
  const [participantsRequired, setParticipantsRequired] = useState(false);
  const [numberOfPeople, setNumberOfPeople] = useState<number | null>(1);
  const [multiplier, setMultiplier] = useState<number | null>(1);
  const [mandatory, setMandatory] = useState(false);
  return (
    <TableBody>
      <TableRow>
        <TableCell />
        <TableCell>
          <TextField label="Nazwa" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell>
          <LabelledCheckbox
            label="Wymagana lista uczestników"
            checked={participantsRequired}
            onChange={setParticipantsRequired}
          />
        </TableCell>
        <TableCell>
          {participantsRequired ? (
            <NumberField label="Liczba osób" fullWidth value={numberOfPeople} onChange={setNumberOfPeople} min={1} />
          ) : (
            <NumberField label="Mnożnik" fullWidth value={multiplier} onChange={setMultiplier} min={1} />
          )}
        </TableCell>
        <TableCell>
          <LabelledCheckbox label="Opcja wymagana" checked={mandatory} onChange={setMandatory} />
        </TableCell>
        <TableCell />
        <TableCell>
          <AsyncLoadingButton
            disabled={!name || (participantsRequired && !numberOfPeople)}
            onClick={async () => {
              await onAdd({
                name,
                roomConfig: participantsRequired ? { numberOfPeople: numberOfPeople! } : null,
                multiplier: participantsRequired ? null : multiplier,
                mandatory,
              });
              setName("");
              setParticipantsRequired(false);
              setNumberOfPeople(1);
              setMultiplier(1);
              setMandatory(false);
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface RoomConfigDialogProps {
  readonly open: boolean;
  readonly config: Pick<ProductOption, "roomConfig" | "name" | "multiplier" | "mandatory">;
  readonly onChange: (option: OptionChange) => Promise<void>;
  readonly onCancel: () => void;
}

const RoomConfigDialog: FC<RoomConfigDialogProps> = ({ open, config, onChange, onCancel }) => {
  const [participantsRequired, setParticipantsRequired] = useState(() => !!config.roomConfig);
  const [participantsNumber, setParticipantsNumber] = useState<number | null>(
    () => config.roomConfig?.numberOfPeople ?? 1,
  );
  const [multiplier, setMultiplier] = useState<number | null>(() => (config.roomConfig ? 1 : (config.multiplier ?? 1)));
  const [name, setName] = useState(() => config.name ?? "");
  const [mandatory, setMandatory] = useState(() => config.mandatory);

  useEffect(() => {
    if (!open) {
      return;
    }
    setParticipantsRequired(!!config.roomConfig);
    setParticipantsNumber(config.roomConfig?.numberOfPeople ?? 1);
    setMultiplier(config.roomConfig ? 1 : (config.multiplier ?? 1));
    setName(config.name ?? "");
    setMandatory(config.mandatory);
  }, [open, config]);

  const handleSave = async () => {
    await onChange({
      name,
      roomConfig: participantsRequired ? { numberOfPeople: participantsNumber! } : null,
      multiplier: participantsRequired ? null : multiplier,
      mandatory,
    });
  };
  const roomUnchanged =
    !!participantsRequired === !!config.roomConfig &&
    (!participantsRequired || participantsNumber === config.roomConfig?.numberOfPeople);
  const multiplierUnchanged = participantsRequired || multiplier === (config.roomConfig ? 1 : (config.multiplier ?? 1));
  const nameUnchanged = !name || name === config.name;
  const mandatoryUnchanged = mandatory === config.mandatory;
  const saveDisabled = roomUnchanged && multiplierUnchanged && nameUnchanged && mandatoryUnchanged;
  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>Modyfikuj opcję</DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            marginTop: 2,
          }}
        >
          <TextField label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
          <LabelledCheckbox
            label="Lista uczestników wymagana"
            checked={participantsRequired}
            onChange={setParticipantsRequired}
          />
          {participantsRequired ? (
            <NumberField label="Liczba osób" value={participantsNumber} min={1} onChange={setParticipantsNumber} />
          ) : (
            <NumberField label="Mnożnik" value={multiplier} min={1} onChange={setMultiplier} />
          )}
          <LabelledCheckbox label="Opcja wymagana" checked={mandatory} onChange={setMandatory} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton onClick={handleSave} disabled={saveDisabled}>
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface ProductOptionRowProps {
  readonly option: ProductOption;
  readonly productsForOption: { productId: string; productName: string }[];
  readonly onUpdateOption: (option: OptionChange) => Promise<void>;
  readonly onDelete: (force: boolean) => Promise<void>;
}

const ProductOptionRow: FC<ProductOptionRowProps> = ({ option, productsForOption, onUpdateOption, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }} data-testid="product-option-row">
        <TableCell>
          <IconButton
            aria-label="szczegóły opcji"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!productsForOption}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{option.name}</TableCell>
        <TableCell>{option.roomConfig ? `tak (${option.roomConfig.numberOfPeople})` : "nie"}</TableCell>
        <TableCell data-testid="dictionary-option-multiplier">{option.multiplier}</TableCell>
        <TableCell data-testid="dictionary-option-mandatory">{option.mandatory ? "tak" : "nie"}</TableCell>
        <TableCell data-testid="dictionary-option-usage-count">{productsForOption.length}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={2}>
            <IconButton size="small" aria-label="Edytuj opcję" onClick={() => setDialogOpen(true)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => setConfirmationDialogOpen(true)} aria-label="Usuń opcję ze słownika">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <UsedByProducts open={open} products={productsForOption} colSpan={7} />
      <RoomConfigDialog
        open={dialogOpen}
        config={option}
        onChange={async (change) => {
          await onUpdateOption(change);
          setDialogOpen(false);
        }}
        onCancel={() => setDialogOpen(false)}
      />
      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Usuń opcję ze słownika"
        content={
          productsForOption.length > 0
            ? `Czy chcesz usunąć opcję ze słownika i usunąć ją z ${productsForOption.length} produktów?`
            : "Czy chcesz usunąć opcję ze słownika? Nie jest ona używana w żadnym produkcie."
        }
        onConfirm={() => onDelete(productsForOption.length > 0)}
        onCancel={() => setConfirmationDialogOpen(false)}
      />
    </>
  );
};

const ProductOptionsDictionary = () => {
  const {
    productOptions,
    isLoading: areOptionsLoading,
    addProductOption,
    updateProductOptionNameAndRoomConfig,
    deleteProductOption,
  } = useProductOptions();
  const { products, isLoading: areProductsLoading } = useProducts();
  const isLoading = areOptionsLoading || areProductsLoading;
  const optionsWithProducts = products.flatMap((p) =>
    p.options.map((o) => ({
      optionName: o.name,
      productName: p.name,
      productId: p.id,
    })),
  );
  const productsForOption = groupBy(optionsWithProducts, ({ optionName }) => optionName);
  return (
    <DictionaryAccordion title="Opcje produktów" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Nazwa</TableCell>
            <TableCell>Wymagana lista osób</TableCell>
            <TableCell>Mnożnik</TableCell>
            <TableCell>Opcja wymagana</TableCell>
            <TableCell>Liczba wystąpień</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddOptionFooter
          onAdd={async (change) => {
            await addProductOption(optionChangeToRequest(change));
          }}
        />
        <TableBody>
          {productOptions.map((o) => (
            <ProductOptionRow
              key={o.name}
              option={o}
              productsForOption={productsForOption[o.name] ?? []}
              onUpdateOption={async (change) => {
                await updateProductOptionNameAndRoomConfig(o.id, optionChangeToRequest(change));
              }}
              onDelete={(force) => deleteProductOption(o.id, force)}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

function optionChangeToRequest({
  name,
  roomConfig,
  multiplier,
  mandatory,
}: OptionChange): CreateOrUpdateProductOptionRequestBody {
  return { name, roomConfig: roomConfig ?? undefined, multiplier: multiplier ?? undefined, mandatory };
}

export default ProductOptionsDictionary;
